import { ChangeDetectionStrategy, Component, LOCALE_ID, computed, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import {
  MibBreakpointService,
  MibTab,
  MibTabsBehaviour,
  MibTabsComponent,
} from '@medinbox/angular';
import { OpenCasesService } from '@core/services';
import { CaseSessionsService } from '@features/case-details/case-sessions.service';

/** What the library's own tab stands for, which is the only case-less page of the bar. */
const LIBRARY = '/';

/**
 * The library and the cases held open, as tabs along the top of the bar.
 *
 * It stands above the pages rather than inside one: the tabs are the way between them, so they are
 * drawn by the bar and survive the page changing under them.
 */
@Component({
  selector: 'mai-case-tabs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MibTabsComponent],
  /* All the room the actions leave, so they always sit at its end. */
  host: { class: 'flex min-w-0 grow-[999] basis-0 self-stretch' },
  templateUrl: './case-tabs.html',
})
export class CaseTabs {
  private readonly cases = inject(OpenCasesService);
  private readonly sessions = inject(CaseSessionsService);
  private readonly router = inject(Router);
  private readonly locale = inject(LOCALE_ID);
  private readonly breakpointService = inject(MibBreakpointService);

  /** The library is always in the row, so the arrow's panel lists only the cases; on a phone, all of them. */
  protected readonly behaviour = computed<MibTabsBehaviour>(() => ({
    panelContentTabs: false,
    closableInPanel: this.breakpointService.sizeClass() !== 'expanded',
  }));

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /*
   The library never closes: it is where every case came from, and where closing the last lands. Every tab
   carries where it leads, so a case opens beside on a ctrl-click as any link of the application does.
  */
  /* Only a case asked for loads: a tab brought back by a reload waits, quiet, until it is read. */
  private readonly loading = computed(
    () =>
      new Set(
        this.sessions
          .all()
          .filter((session) => session.loading())
          .map((session) => session.id),
      ),
  );

  readonly tabs = computed<MibTab[]>(() => [
    {
      value: LIBRARY,
      label: 'Library',
      subLabel: 'Case library',
      icon: 'library-big',
      iconOnly: true,
      fit: 'content',
      href: LIBRARY,
    },
    ...this.cases.opened().map((held) => ({
      value: held.id,
      label: held.title,
      /* The application's own date format, as the library's cards write it, never the browser's. */
      subLabel: held.createdAt ? formatDate(held.createdAt, 'mediumDate', this.locale) : undefined,
      href: `/video-details/${held.id}`,
      closable: true,
      loading: this.loading().has(held.id),
    })),
  ]);

  /** The URL says which tab is open, so a case reached from anywhere lights its own. */
  protected readonly reading = computed(() => {
    const match = /^\/video-details\/([^/?#]+)/.exec(this.url());
    return match ? match[1] : LIBRARY;
  });

  protected open(value: string | undefined): void {
    void this.router.navigateByUrl(value === LIBRARY || !value ? '/' : `/video-details/${value}`);
  }

  /* As a browser does: the case before takes over, the one after when it was first, else the library. */
  protected close(id: string): void {
    const held = this.cases.opened();
    const index = held.findIndex((candidate) => candidate.id === id);
    const next = held[index - 1] ?? held[index + 1];
    const reading = this.reading() === id;
    this.cases.close(id);

    if (reading) {
      this.open(next?.id);
    }
  }
}
